import React, { useState, useMemo } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import RankForm from '../../components/HR/RankForm';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { Rank } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import { useRanks, useCreateRank, useUpdateRank, useDeleteRank } from '../../hooks/useRanks';
import TableSkeleton from '../../components/Loading/TableSkeleton';

const Ranks: React.FC = () => {
  const queryOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'name', order: 'asc' as const }]
  }), []);

  const { data: ranks, isLoading, error, refetch } = useRanks(queryOptions);

  const createMutation = useCreateRank(() => { refetch(); });
  const updateMutation = useUpdateRank(() => { refetch(); });
  const deleteMutation = useDeleteRank(() => { refetch(); });

  const [showForm, setShowForm] = useState(false);
  const [editingRank, setEditingRank] = useState<Rank | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [crewFilter, setCrewFilter] = useState<string>('all');

  const uniqueDepartments = useMemo(() => {
    if (!ranks || ranks.length === 0) return [];
    return Array.from(new Set(ranks.map(rank => rank.department).filter(Boolean)));
  }, [ranks]);

  const columns: TableColumn[] = [
    { key: 'code', label: 'Rank Code', sortable: true },
    { key: 'name', label: 'Rank Name', sortable: true },
    { key: 'arabicName', label: 'Arabic Name', sortable: true },
    { key: 'department', label: 'Department', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value === 'Navigation' ? 'bg-blue-100 text-blue-800' :
        value === 'Engineering' ? 'bg-green-100 text-green-800' :
        value === 'Deck' ? 'bg-orange-100 text-orange-800' :
        'bg-gray-100 text-gray-800'
      }`}>{value}</span>
    ) },
    { key: 'description', label: 'Description', sortable: true },
    { key: 'isCrew', label: 'Is Crew', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
      }`}>{value ? 'Crew' : 'Non-Crew'}</span>
    ) }
  ];

  const filteredRanks = useMemo(() => {
    if (!ranks || ranks.length === 0) return [];
    return ranks.filter(rank => {
      const matchesSearch = !searchTerm || (
        rank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rank.arabicName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rank.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rank.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesDepartment = departmentFilter === 'all' || rank.department === departmentFilter;
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && rank.isActive) ||
        (statusFilter === 'inactive' && !rank.isActive);
      const matchesCrew = crewFilter === 'all' ||
        (crewFilter === 'crew' && rank.isCrew) ||
        (crewFilter === 'non-crew' && !rank.isCrew);
      return matchesSearch && matchesDepartment && matchesStatus && matchesCrew;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [ranks, searchTerm, departmentFilter, statusFilter, crewFilter]);

  const handleExport = () => {
    const exportData = filteredRanks.map(rank => ({
      'Rank Code': rank.code, 'Rank Name': rank.name, 'Arabic Name': rank.arabicName,
      'Department': rank.department, 'Description': rank.description, 'Is Crew': rank.isCrew ? 'Yes' : 'No',
      'Created Date': new Date(rank.createdAt).toLocaleDateString(), 'Updated Date': new Date(rank.updatedAt).toLocaleDateString()
    }));
    exportToExcel(exportData, `ranks_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredRanks.map(rank => ({
      'Rank Code': rank.code, 'Rank Name': rank.name, 'Arabic Name': rank.arabicName,
      'Department': rank.department, 'Description': rank.description, 'Is Crew': rank.isCrew ? 'Yes' : 'No',
      'Created Date': new Date(rank.createdAt).toLocaleDateString(), 'Updated Date': new Date(rank.updatedAt).toLocaleDateString()
    }));
    printAsExcel(exportData, 'Ranks');
  };

  const handleAdd = () => { setEditingRank(undefined); setShowForm(true); };
  const handleEdit = (rank: Rank) => { setEditingRank(rank); setShowForm(true); };
  const handleDelete = async (rank: Rank) => {
    if (window.confirm(`Are you sure you want to delete the rank "${rank.name}"?`)) {
      await deleteMutation.execute(rank);
    }
  };

  const handleFormSubmit = async (rankData: Partial<Rank>) => {
    if (editingRank) {
      await updateMutation.execute({ id: editingRank.id, data: { ...rankData, documentId: editingRank.documentId } });
    } else {
      await createMutation.execute(rankData);
      await bumpCodePrefixSequence(rankData.code);
    }
    setShowForm(false);
    setEditingRank(undefined);
  };

  const handleFormCancel = () => { setShowForm(false); setEditingRank(undefined); };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('all');
    setStatusFilter('all');
    setCrewFilter('all');
  };

  const filterConfigs: FilterConfig[] = [
    { key: 'department', label: 'Department', type: 'select', value: departmentFilter, onChange: setDepartmentFilter, options: [
      { value: 'all', label: 'All Departments' }, ...uniqueDepartments.map(d => ({ value: d, label: d }))
    ]},
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }
    ]},
    { key: 'crew', label: 'Type', type: 'select', value: crewFilter, onChange: setCrewFilter, options: [
      { value: 'all', label: 'All Types' }, { value: 'crew', label: 'Crew Ranks' }, { value: 'non-crew', label: 'Non-Crew Ranks' }
    ]},
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Ranks Management" description="Manage maritime ranks, levels, and requirements for your organization." />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Ranks</dt>
                  <dd className="text-lg font-medium text-gray-900">{filteredRanks.length} / {ranks?.length || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Ranks</dt>
                  <dd className="text-lg font-medium text-gray-900">{filteredRanks.filter(r => r.isActive).length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Departments</dt>
                  <dd className="text-lg font-medium text-gray-900">{new Set(filteredRanks.map(r => r.department)).size}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Crew Ranks</dt>
                  <dd className="text-lg font-medium text-gray-900">{filteredRanks.filter(r => r.isCrew).length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!showForm && (
        <>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search ranks..."
            filters={filterConfigs}
            onClearFilters={handleClearFilters}
            totalCount={ranks?.length || 0}
            filteredCount={filteredRanks.length}
            entityName="Ranks"
            onAdd={handleAdd}
            addLabel="Add Rank"
            onExport={handleExport}
            onPrint={handlePrint}
          />
          <div className="card">
            {isLoading ? (
              <TableSkeleton />
            ) : error ? (
              <ErrorState message={error.message || String(error)} onRetry={() => refetch()} entityName="ranks" />
            ) : (
              <Table data={filteredRanks} columns={columns} onEdit={handleEdit} onDelete={handleDelete} showAddButton={false} searchable={false} />
            )}
          </div>
        </>
      )}
      {showForm && (
        <RankForm rank={editingRank} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      )}
    </div>
  );
};

export default Ranks;
